<template>
  <div class="row content-box full-height">
    <woot-wizard
      class="hide-for-small-only medium-3 columns"
      :global-config="globalConfig"
      :items="items"
    />
    <router-view />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import router from '../../../index';
import { isEmbeddedSignupAvailable } from './channels/whatsapp/embeddedSignupUtils';
import { INBOX_CHANNEL_FORM_ROUTE } from './helpers/whatsappConnectGuard';

// Primera ruta con path ":sub_page": es donde vue-router cae al resolver solo por URL.
const AMBIGUOUS_ROUTE = 'settings_inbox_conditions';

export default {
  mixins: [globalConfigMixin],
  data() {
    return {
      channelSelected: '',
    };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
    // Sobre $route (reactivo) y no window.location: así los pasos del wizard siguen la navegación.
    getCurrentURL() {
      return this.$route.fullPath;
    },
    // Pasos del wizard lateral según el canal: Evolution y WhatsApp Cloud (con Embedded Signup)
    // tienen su propio flujo; el resto usa el genérico. El canal recordado en channelSelected
    // mantiene el flujo cuando la URL ya no lo trae (pasos de agentes y finalizar).
    items() {
      const showWhatsappBusinessFlow = this.getCurrentURL.includes(
        'new/evolution_api'
      );
      // WhatsApp Cloud con Embedded Signup agrega el paso de elegir la forma de conexión.
      const showWhatsappCloudFlow =
        isEmbeddedSignupAvailable() &&
        (this.getCurrentURL.includes('new/whatsapp') ||
          this.channelSelected === 'whatsapp');
      let wizard = 'INBOX_MGMT.CREATE_FLOW';
      if (
        (this.channelSelected === '' && showWhatsappBusinessFlow) ||
        this.channelSelected === 'evolution_api'
      ) {
        wizard = 'INBOX_MGMT.ADD.EVOLUTION_API.CREATE_FLOW';
      } else if (showWhatsappCloudFlow) {
        wizard = 'INBOX_MGMT.ADD.WHATSAPP.CREATE_FLOW';
      }
      return this.$t(wizard).map(item => ({
        ...item,
        body: this.useInstallationName(
          item.body,
          this.globalConfig.installationName
        ),
      }));
    },
  },
  watch: {
    // Cada navegación dentro del alta: recordar el canal y corregir la ruta ambigua.
    $route(route) {
      this.rememberChannelFromRoute(route);
      this.normalizeAmbiguousRoute(route);
    },
  },
  mounted() {
    bus.$on(BUS_EVENTS.CREATE_CHANNEL_FLOW, channel => {
      this.channelSelected = channel;
    });
    this.rememberChannelFromRoute(this.$route);
    this.normalizeInitialRoute();
  },
  methods: {
    // Guarda el canal que aparece en "/inboxes/new/:canal" (también con recarga o URL directa),
    // para que el wizard conserve su flujo en los pasos siguientes, cuya URL ya no trae el canal.
    rememberChannelFromRoute(route) {
      const match = (route.fullPath || '').match(/\/inboxes\/new\/([a-z_]+)/);
      if (match) this.channelSelected = match[1];
    },
    // En una recarga (F5) sobre "/inboxes/new/:canal" vue-router cae en la primera ruta con ese
    // path, la de condiciones de Evolution API. Para cualquier otro canal se reemplaza por la
    // ruta del formulario del canal. Las rutas con path propio (agentes, finalizar, conexión de
    // WhatsApp) ya resuelven bien y no traen sub_page, así que no entran aquí.
    normalizeInitialRoute() {
      const isCreatingChannel = this.getCurrentURL.includes('/inboxes/new');
      const isEvolutionFlow = this.getCurrentURL.includes('new/evolution_api');
      const channel = this.$route.params.sub_page;

      if (
        !isCreatingChannel ||
        isEvolutionFlow ||
        !channel ||
        this.$route.name === INBOX_CHANNEL_FORM_ROUTE
      ) {
        return;
      }

      router.replace({
        name: INBOX_CHANNEL_FORM_ROUTE,
        params: { sub_page: channel },
      });
    },
    // Varias rutas del wizard comparten el path ":sub_page"; al navegar con el historial del
    // navegador (Atrás/Adelante) vue-router resuelve por path y cae en la primera, la de
    // condiciones de Evolution API, con lo que el wizard pierde el paso activo. Para los demás
    // canales se reemplaza por la ruta del formulario sin tocar el historial. El replace vuelve
    // a disparar este watcher con la ruta destino, que ya no es la ambigua, así que termina ahí;
    // el guard de la ruta de conexión tampoco lo activa porque redirige a la misma ruta destino.
    normalizeAmbiguousRoute(route) {
      if (route.name !== AMBIGUOUS_ROUTE) return;
      if (route.params.sub_page === 'evolution_api') return;

      router.replace({
        name: INBOX_CHANNEL_FORM_ROUTE,
        params: route.params,
        query: route.query,
        hash: route.hash,
      });
    },
  },
};
</script>
